import { Card, CardContent, Typography } from '@mui/material';
import {
  LineChart,
  LineChartProps,
  mangoFusionPalette,
} from '@mui/x-charts-pro';
import { ReactNode } from 'react';
import {
  ChartsHighlightProvider,
  useChartsHighlight,
} from '../inc-charts.context';
import HighlightedLegend from './legend/highlighted-legend.component';
import HighlightedLine from './line/highlighted-line.component';
import HighlightedTooltip from './tooltip/highlighted-tooltip.component';

export type IncLineChartProps = {
  series: LineChartProps['series'];
  xlabels?: string[];
  title: ReactNode;
  LineChartProps?: Partial<LineChartProps>;
  'data-testid'?: string;
};

const IncLineChart = ({
  series = [],
  xlabels = [],
  title,
  LineChartProps = {},
  'data-testid': dataTestId,
}: IncLineChartProps) => {
  const chartsHighlightContext = useChartsHighlight();

  return (
    <Card>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        <Typography
          variant="body2"
          component="div"
          margin={0}
          data-testid="IncLineChart.YAxisLabel"
        >
          {title}
        </Typography>
        <ChartsHighlightProvider value={chartsHighlightContext}>
          <LineChart
            data-testid={dataTestId}
            margin={{ bottom: 8 }}
            series={series}
            xAxis={[{ scaleType: 'point', data: xlabels }]}
            yAxis={[{ id: 'leftAxisId' }, { id: 'rightAxisId' }]}
            colors={mangoFusionPalette}
            onHighlightChange={chartsHighlightContext.setHighlightedItem}
            highlightedItem={chartsHighlightContext.highlightedItem}
            {...LineChartProps}
            slotProps={{
              legend: {
                position: {
                  horizontal: 'center',
                  vertical: 'bottom',
                },
              },
              tooltip: {
                trigger: 'axis', // item doesn't work for line charts
              },
              ...LineChartProps.slotProps,
            }}
            slots={{
              legend: HighlightedLegend,
              line: HighlightedLine,
              tooltip: HighlightedTooltip,
              ...LineChartProps.slots,
            }}
          ></LineChart>
        </ChartsHighlightProvider>
      </CardContent>
    </Card>
  );
};

export default IncLineChart;
